import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector, useDispatch } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import { updatepageName } from '../../Redux/page';
import {
  GrowthModal,
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  GrowthPageLoader,
  GrowthFormGrid,
  GrowthFormSwitch,
} from '../growth_hub/GrowthModal';

/**
 * The flat fee a hub adds to every order.
 *
 * The backend for this shipped with migration 0117 — model, viewset, resolver
 * and a realtime push — and there has never been a screen to drive it. The fee
 * could only be set through Django admin or a raw API call, which is why it was
 * ₹0 everywhere.
 *
 * Deliberately the same shape as the wallet checkout config beside it: one row
 * per hub, a missing row meaning off. Two config screens that behave differently
 * is how an admin learns to distrust both.
 */
const emptyForm = {
  inventory: '',
  is_active: false,
  amount: '0',
  label: 'Platform Fee',
};

/** Matches PlatformFeeConfigSerializer.validate_amount, so the server never has to say no. */
const MAX_FEE = 1000;

export function PlatformFeeConfig() {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const inventoryList = useSelector((state) => state.inventory.inventory) || [];

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const showToast = (severity, summary, detail) =>
    toast.current?.show({ severity, summary, detail, life: 3000 });

  const hubLabel = (id) => {
    const hub = inventoryList.find(
      (inv) => Number(inv.value ?? inv.id) === Number(id)
    );
    return hub?.name || hub?.place || `Hub #${id}`;
  };

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await CustomAxios.get('platform-fee-config/');
      const rows = Array.isArray(res?.data) ? res.data : res?.data?.results || [];
      setConfigs(rows);
    } catch (err) {
      showToast('error', 'Error', 'Could not load platform fee configs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(updatepageName('Platform Fee'));
    fetchConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = () => {
    setEditMode(false);
    setSelectedId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditMode(true);
    setSelectedId(row.id);
    setForm({
      inventory: row.inventory,
      is_active: Boolean(row.is_active),
      amount: String(row.amount ?? '0'),
      label: row.label || 'Platform Fee',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.inventory) {
      showToast('warn', 'Validation', 'Select a hub');
      return;
    }

    const amount = Number(form.amount);
    if (!Number.isFinite(amount) || amount < 0) {
      showToast('warn', 'Validation', 'Fee must be 0 or more');
      return;
    }
    if (amount > MAX_FEE) {
      showToast('warn', 'Validation', `Fee above ₹${MAX_FEE} looks like a typo`);
      return;
    }
    /*
     * An "active" fee of zero draws a ₹0.00 line on every bill — noise
     * pretending to be information. The backend already refuses to treat it as
     * active; saying so here means the admin finds out before they save.
     */
    if (form.is_active && amount <= 0) {
      showToast('warn', 'Validation', 'An active fee needs an amount above ₹0');
      return;
    }

    const payload = {
      inventory: Number(form.inventory),
      is_active: Boolean(form.is_active),
      amount: form.amount || '0',
      label: (form.label || 'Platform Fee').trim().slice(0, 40),
    };

    try {
      setSaving(true);
      if (editMode && selectedId) {
        await CustomAxios.patch(`platform-fee-config/${selectedId}/`, payload);
        showToast('success', 'Saved', 'Platform fee updated');
      } else {
        await CustomAxios.post('platform-fee-config/', payload);
        showToast('success', 'Created', 'Platform fee created');
      }
      setModalOpen(false);
      fetchConfigs();
    } catch (err) {
      const data = err?.response?.data || {};
      const detail =
        data.amount?.[0] ||
        data.inventory?.[0] ||
        data.non_field_errors?.[0] ||
        data.detail ||
        'Save failed';
      showToast('error', 'Error', String(detail));
    } finally {
      setSaving(false);
    }
  };

  const configuredHubIds = new Set(configs.map((c) => Number(c.inventory)));

  return (
    <Box sx={{ padding: '30px' }}>
      <Toast ref={toast} />

      <Box sx={{ mb: 2.5, mt: 1.5 }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, lineHeight: '24px' }}>
          Platform Fee
        </Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}>
          A flat charge added to every order from a hub. No row means no fee.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none' }}
          onClick={openAdd}
        >
          Add hub fee
        </Button>
      </Box>

      {loading ? (
        <GrowthPageLoader label="Loading platform fee configs…" />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Hub</TableCell>
                <TableCell>Fee</TableCell>
                <TableCell>Label on the bill</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    No hub charges a platform fee. Add one to start.
                  </TableCell>
                </TableRow>
              )}
              {configs.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.inventory_place || hubLabel(row.inventory)}</TableCell>
                  <TableCell>₹ {Number(row.amount || 0).toFixed(2)}</TableCell>
                  <TableCell>{row.label || 'Platform Fee'}</TableCell>
                  <TableCell>
                    {/*
                      "Off" and "Off (₹0)" are different facts: a hub deliberately
                      switched off, and one that is on with nothing set — which
                      charges nothing either, and is almost always a mistake.
                    */}
                    {row.is_active && Number(row.amount) > 0
                      ? 'Charging'
                      : row.is_active
                        ? 'On, but ₹0'
                        : 'Off'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <GrowthModal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editMode ? 'Edit platform fee' : 'Add platform fee'}
        subtitle="A flat charge added to every order from this hub"
        maxWidth={{ xs: '94%', sm: 440 }}
        busy={saving}
        busyLabel={editMode ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={handleSave} loading={saving}>
              {editMode ? 'Save' : 'Create'}
            </GrowthPrimaryButton>
          </>
        }
      >
        <GrowthFormGrid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="pf-hub">Hub</InputLabel>
              <Select
                labelId="pf-hub"
                label="Hub"
                value={form.inventory}
                disabled={editMode}
                onChange={(e) => setForm({ ...form, inventory: e.target.value })}
              >
                {inventoryList
                  // One row per hub — the model is a OneToOne, so offering a hub
                  // that already has one only produces a 400.
                  .filter(
                    (inv) =>
                      editMode ||
                      !configuredHubIds.has(Number(inv.value ?? inv.id))
                  )
                  .map((inv) => (
                    <MenuItem key={inv.value ?? inv.id} value={inv.value ?? inv.id}>
                      {inv.name || inv.place}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <GrowthFormSwitch
              label="Charge a platform fee at this hub"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Fee (₹)"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              inputProps={{ min: 0, max: MAX_FEE, step: 0.01 }}
              helperText="Flat rupees, added to every order from this hub."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Label on the bill"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              inputProps={{ maxLength: 40 }}
              helperText="What the customer sees. Max 40 characters."
            />
          </Grid>

          {form.is_active && Number(form.amount) > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Every order from {form.inventory ? hubLabel(form.inventory) : 'this hub'} will
                show “{(form.label || 'Platform Fee').trim()} ₹
                {Number(form.amount).toFixed(2)}” on the bill.
              </Typography>
            </Grid>
          )}

          {form.is_active && Number(form.amount) <= 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="warning.main">
                Switched on with no amount — nothing will be charged and no line
                will appear on the bill.
              </Typography>
            </Grid>
          )}
        </GrowthFormGrid>
      </GrowthModal>
    </Box>
  );
}

export default PlatformFeeConfig;
